import * as CookieConsent from 'vanilla-cookieconsent'
import { syncAnalytixConsent } from './analytixTracking.js'

export const COOKIE_CATEGORIES = {
  NECESSARY: 'necessary',
  EXTERNAL: 'external',
  ANALYTICS: 'analytics',
}

const consentListeners = new Set()
let analyticsInitialized = false

export function hasCookieConsent(category) {
  try {
    return CookieConsent.acceptedCategory(category)
  } catch {
    return category === COOKIE_CATEGORIES.NECESSARY
  }
}

export function onCookieConsentChange(listener) {
  consentListeners.add(listener)
  return () => consentListeners.delete(listener)
}

function emitConsentChange() {
  consentListeners.forEach((listener) => listener())
}

export function openCookieSettings() {
  CookieConsent.showPreferences()
}

function loadScript(src, id) {
  return new Promise((resolve, reject) => {
    if (id && document.getElementById(id)) {
      resolve()
      return
    }

    const script = document.createElement('script')
    script.src = src
    script.async = true
    if (id) script.id = id
    script.onload = () => resolve()
    script.onerror = reject
    document.head.appendChild(script)
  })
}

let mapboxStylesheetPromise = null

export function loadMapboxStylesheet() {
  if (!mapboxStylesheetPromise) {
    mapboxStylesheetPromise = import('mapbox-gl/dist/mapbox-gl.css')
  }
  return mapboxStylesheetPromise
}

export function initAnalyticsIfConsented() {
  const measurementId = process.env.VUE_APP_GA_MEASUREMENT_ID
  if (!measurementId || !hasCookieConsent(COOKIE_CATEGORIES.ANALYTICS)) return
  if (analyticsInitialized || window.__fewoAnalyticsInitialized) return

  window.__fewoAnalyticsInitialized = true
  analyticsInitialized = true

  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag() {
    window.dataLayer.push(arguments)
  }
  window.gtag('js', new Date())
  window.gtag('config', measurementId, { anonymize_ip: true })

  loadScript(
    `https://www.googletagmanager.com/gtag/js?id=${measurementId}`,
    'fewo-gtag'
  ).catch((error) => {
    console.warn('[CookieConsent] Analytics konnte nicht geladen werden:', error)
    analyticsInitialized = false
    window.__fewoAnalyticsInitialized = false
  })
}

export function handleConsentUpdate() {
  const analyticsAllowed = hasCookieConsent(COOKIE_CATEGORIES.ANALYTICS)
  syncAnalytixConsent(analyticsAllowed)
  initAnalyticsIfConsented()
  emitConsentChange()
}

export function initCookieConsent() {
  CookieConsent.run({
    categories: {
      necessary: { readOnly: true },
      external: { enabled: false, readOnly: false },
      analytics: { enabled: false, readOnly: false },
    },
    language: {
      default: 'de',
      translations: {
        de: {
          consentModal: {
            title: 'Diese Webseite verwendet Cookies',
            description:
              'Wir verwenden Cookies und externe Dienste (z. B. Karte, Sicherheitsprüfung), um unsere Website bereitzustellen. Statistik-Cookies helfen uns, die Nutzung zu verstehen. Sie entscheiden selbst, was Sie zulassen.',
            acceptAllBtn: 'Alle akzeptieren',
            acceptNecessaryBtn: 'Nur notwendige',
            showPreferencesBtn: 'Einstellungen',
          },
          preferencesModal: {
            title: 'Cookie-Einstellungen',
            acceptAllBtn: 'Alle akzeptieren',
            acceptNecessaryBtn: 'Nur notwendige',
            savePreferencesBtn: 'Speichern',
            closeIconLabel: 'Schließen',
            sections: [
              {
                title: 'Notwendige Cookies',
                description:
                  'Erforderlich für den Betrieb der Website (z. B. Speicherung Ihrer Cookie-Auswahl).',
                linkedCategory: 'necessary',
              },
              {
                title: 'Externe Dienste',
                description:
                  'Mapbox (Karte) und Friendly Captcha (Spam-Schutz in Formularen). Daten können an diese Anbieter übermittelt werden.',
                linkedCategory: 'external',
              },
              {
                title: 'Statistik & Analyse',
                description:
                  'First-Party-Auswertung über MyPortal (Seitenaufrufe, Buchungsanfragen) sowie optional Google Analytics, sofern konfiguriert. Keine Werbe-Cookies.',
                linkedCategory: 'analytics',
              },
            ],
          },
        },
      },
    },
    guiOptions: {
      consentModal: { layout: 'box', position: 'bottom center' },
    },
    cookie: { expiresAfterDays: 365 },
    onConsent: handleConsentUpdate,
    onChange: handleConsentUpdate,
  })

  if (CookieConsent.validConsent()) {
    handleConsentUpdate()
  }
}
